import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { MetricCard } from '../components/ui/KpiCard';
import { useToast } from '../context/ToastContext';
import type { Client } from './Clients';

interface InvoiceLine { label: string; qty: number; unit_price: number; amount: number; }
interface InvoiceContract {
  type?: string; allocations?: Record<string, number>;
  deposit_amount?: number; deposit_received?: boolean; review_date?: string;
}
interface InvoicePreview {
  lines: InvoiceLine[]; subtotal: number; vat: number; total: number; currency: string;
  partial?: boolean; contract?: InvoiceContract;
}
interface InvoiceRow { number: string; client_id: string; month: string; status: string; }

const CONNECTORS = [
  { id: 'csv_accounting', label: 'Export comptable CSV (générique)' },
  { id: 'comptapro', label: 'ComptaPro (journal d’écritures)' },
];

export function Invoices() {
  const toast = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState('');
  const [month, setMonth] = useState('');
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [previewErr, setPreviewErr] = useState('');
  const [list, setList] = useState<InvoiceRow[] | null>(null);
  const [connector, setConnector] = useState('csv_accounting');
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ text: string; err: boolean } | null>(null);

  useEffect(() => { api<Client[]>('/v1/clients').then(setClients).catch(() => {}); }, []);

  async function exportMonth() {
    if (!month) { setExportMsg({ text: 'Choisissez un mois.', err: true }); return; }
    setExportBusy(true); setExportMsg(null);
    try {
      const d = await api<{ connector: string; content: string }>(
        `/v1/exports/invoices?month=${encodeURIComponent(month)}&connector=${encodeURIComponent(connector)}`);
      const blob = new Blob([d.content], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `caissepilot_${connector}_${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setExportMsg({ text: 'Export téléchargé.', err: false });
    } catch (e) { setExportMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
    finally { setExportBusy(false); }
  }

  async function loadPreview() {
    setPreviewErr(''); setPreview(null);
    if (!clientId || !month) { setPreviewErr('Sélectionnez un client et un mois.'); return; }
    try { setPreview(await api<InvoicePreview>(`/v1/invoices/preview?client_id=${encodeURIComponent(clientId)}&month=${encodeURIComponent(month)}`)); }
    catch (e) { setPreviewErr(e instanceof ApiError ? e.message : 'Erreur'); }
  }
  async function issue() {
    if (!clientId || !month) { toast('Sélectionnez un client et un mois', true); return; }
    if (!confirm(`Émettre la facture de ${month} pour ce client ? Cette action est définitive.`)) return;
    try { await api('/v1/invoices', { method: 'POST', body: { client_id: clientId, month } }); toast('Facture émise'); loadList(); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }
  async function loadList() {
    try { setList(await api<InvoiceRow[]>('/v1/invoices' + (month ? '?month=' + encodeURIComponent(month) : ''))); }
    catch { setList([]); }
  }

  return (
    <>
      <div className="pagehead"><h1>Factures</h1></div>
      <div className="card">
        <h2>Aperçu / émission</h2>
        <label>Client</label>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">—</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label>Mois (AAAA-MM)</label>
        <input value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-09" />
        <div className="row" style={{ marginTop: '0.8rem' }}>
          <button className="primary" onClick={loadPreview}>Voir l'aperçu</button>
          <button onClick={issue}>Émettre (mois clos)</button>
        </div>
        {previewErr && <div className="err" style={{ marginTop: '1rem' }}>{previewErr}</div>}
        {preview && (
          <div style={{ marginTop: '1rem' }}>
            <div className="tblwrap"><table><tbody>
              <tr><th>Ligne</th><th>Qté</th><th>PU</th><th>Montant</th></tr>
              {preview.lines.map((l, idx) => (
                <tr key={idx}><td>{l.label}</td><td>{l.qty}</td><td>{l.unit_price}</td><td>{l.amount}</td></tr>
              ))}
            </tbody></table></div>
            <div className="grid2" style={{ marginTop: '1rem' }}>
              <MetricCard label="Sous-total" value={`${preview.subtotal} ${preview.currency}`} />
              <MetricCard label="TVA" value={`${preview.vat} ${preview.currency}`} />
              <MetricCard label="Total" value={`${preview.total} ${preview.currency}`} />
            </div>
            {preview.partial && <p className="muted" style={{ marginTop: '0.6rem' }}>Aperçu partiel — mois non clos.</p>}
            {preview.contract && (
              <p className="muted" style={{ marginTop: '0.6rem' }}>
                Contrat : {preview.contract.type || '—'}
                {' · '}Dotation : {preview.contract.allocations && Object.keys(preview.contract.allocations).length
                  ? Object.entries(preview.contract.allocations).map(([k, v]) => `${k} ${v}`).join(', ') : '—'}
                {' · '}Caution : {preview.contract.deposit_amount ?? 0} {preview.currency}
                {preview.contract.deposit_received ? ' ✓' : ' (en attente)'}
                {preview.contract.review_date && <> · Révision : {preview.contract.review_date}</>}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="card">
        <h2>Export comptable</h2>
        <label htmlFor="invConnector">Connecteur</label>
        <select id="invConnector" value={connector} onChange={(e) => setConnector(e.target.value)}>
          {CONNECTORS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <p className="muted" style={{ marginTop: '0.4rem' }}>Utilise le mois sélectionné ci-dessus.</p>
        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button className="primary" onClick={exportMonth} disabled={exportBusy}>Télécharger l'export</button>
        </div>
        {exportMsg && <div className={exportMsg.err ? 'err' : 'ok'}>{exportMsg.text}</div>}
      </div>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Factures émises</h2>
          <button className="small" onClick={loadList}>Actualiser</button>
        </div>
        {!list && <div className="muted">Choisissez un mois ci-dessus puis actualisez.</div>}
        {list && list.length === 0 && <span className="muted">Aucune facture émise.</span>}
        {list && list.length > 0 && (
          <div className="tblwrap"><table><tbody>
            <tr><th>Numéro</th><th>Client</th><th>Mois</th><th>Statut</th></tr>
            {list.map((inv, idx) => (
              <tr key={idx}><td>{inv.number}</td><td>{inv.client_id}</td><td>{inv.month}</td><td>{inv.status}</td></tr>
            ))}
          </tbody></table></div>
        )}
      </div>
    </>
  );
}
